import { createSelector } from 'reselect';
import { getIsCompanyLoading } from 'lib/selectors/getCompany';
import { getAreAnnotationsLoading } from 'lib/selectors/getAnnotations';
import { getIsInitialActivitySetPollLoading } from 'lib/selectors/getIsInitialActivitySetPollLoading';
import { getAreActivityTypesLoading } from 'lib/selectors/getActivityTypes';
import { getPostLaborUpdatesOnNats } from 'lib/selectors/getPostLaborUpdatesOnNats';
import { getLaborTicketsLoading } from 'lib/selectors/getLaborTicketsLoading';
import { getEventsLoading } from 'lib/selectors/getEventsLoading';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';

const getIsBaseAppDataLoading = createSelector(
  [
    (state) => {
      return state.loading;
    },
    (state) => {
      return state.shifts.loading;
    },
    getIsCompanyLoading,
    getAreAnnotationsLoading,
    getIsInitialActivitySetPollLoading,
    getAreActivityTypesLoading,
    getPostLaborUpdatesOnNats,
    getLaborTicketsLoading,
    getEventsLoading,
    getIsApmERPJobFlow,
  ],
  (
    isInitialDataLoading,
    areShiftsLoading,
    isCompanyLoading,
    areAnnotationsLoading,
    isInitialActivitySetPollLoading,
    areActivityTypesLoading,
    natsFlowEnabled,
    laborTicketsLoading,
    eventsLoading,
    isApmERPJobFlow
  ) => {
    // APM mode: don't wait on activity sets or labor ticket subscription
    // (opdash display MFE manages current job)
    if (isApmERPJobFlow) {
      return !!(
        isInitialDataLoading ||
        areShiftsLoading ||
        isCompanyLoading ||
        areAnnotationsLoading ||
        areActivityTypesLoading ||
        eventsLoading
      );
    }

    // NATS mode: wait for labor tickets instead of activity sets
    if (natsFlowEnabled) {
      return !!(
        isInitialDataLoading ||
        areShiftsLoading ||
        isCompanyLoading ||
        areAnnotationsLoading ||
        areActivityTypesLoading ||
        laborTicketsLoading
      );
    }

    return !!(
      isInitialDataLoading ||
      areShiftsLoading ||
      isCompanyLoading ||
      areAnnotationsLoading ||
      isInitialActivitySetPollLoading ||
      areActivityTypesLoading
    );
  }
);

const getBaseAppDataLoadingStates = createSelector(
  [
    (state) => {
      return state.loading;
    },
    (state) => {
      return state.shifts.loading;
    },
    getIsCompanyLoading,
    getAreAnnotationsLoading,
    getIsInitialActivitySetPollLoading,
    getAreActivityTypesLoading,
    getPostLaborUpdatesOnNats,
    getLaborTicketsLoading,
    getEventsLoading,
    getIsApmERPJobFlow,
  ],
  (
    isInitialDataLoading,
    areShiftsLoading,
    isCompanyLoading,
    areAnnotationsLoading,
    isInitialActivitySetPollLoading,
    areActivityTypesLoading,
    natsFlowEnabled,
    laborTicketsLoading,
    eventsLoading,
    isApmERPJobFlow
  ) => {
    // APM mode: no activity set or labor ticket subscription dependency
    if (isApmERPJobFlow) {
      return {
        initialData: isInitialDataLoading,
        shifts: areShiftsLoading,
        company: isCompanyLoading,
        annotations: areAnnotationsLoading,
        activityTypes: areActivityTypesLoading,
        events: eventsLoading,
      };
    }

    // NATS mode: include labor tickets loading instead of activity set
    if (natsFlowEnabled) {
      return {
        initialData: isInitialDataLoading,
        shifts: areShiftsLoading,
        company: isCompanyLoading,
        annotations: areAnnotationsLoading,
        activityTypes: areActivityTypesLoading,
        laborTickets: laborTicketsLoading,
      };
    }

    return {
      initialData: isInitialDataLoading,
      shifts: areShiftsLoading,
      company: isCompanyLoading,
      annotations: areAnnotationsLoading,
      latestActivitySet: isInitialActivitySetPollLoading,
      activityTypes: areActivityTypesLoading,
    };
  }
);

export { getIsBaseAppDataLoading, getBaseAppDataLoadingStates };
